import React, { useState } from 'react';
import { ExchangeMeta } from './data';

export const MonoTag: React.FC<{
  children: React.ReactNode;
  color?: string;
  size?: number;
  style?: React.CSSProperties;
}> = ({ children, color = '#9a9a9a', size = 11, style }) => (
  <span
    style={{
      fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
      fontSize: size,
      color,
      letterSpacing: '.22em',
      textTransform: 'uppercase',
      ...style,
    }}
  >
    {children}
  </span>
);

export const AmButton: React.FC<{
  children: React.ReactNode;
  ghost?: boolean;
  disabled?: boolean;
  full?: boolean;
  style?: React.CSSProperties;
  onClick?: () => void;
}> = ({ children, ghost, disabled, full, style, onClick }) => {
  const [hover, setHover] = useState(false);
  const active = !disabled && hover;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        height: 46,
        padding: '0 24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: ghost
          ? 'transparent'
          : disabled
          ? '#383838'
          : active
          ? '#00C870'
          : '#00B061',
        color: ghost ? '#fff' : disabled ? '#787878' : '#000',
        fontFamily: 'Kanit',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '.18em',
        textTransform: 'uppercase',
        border: ghost ? `1px solid ${active ? '#fff' : '#787878'}` : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: full ? '100%' : undefined,
        boxShadow: active && !ghost ? '0 0 22px rgba(0,176,97,.55)' : 'none',
        transform: active ? 'translateY(-1px)' : 'none',
        transition:
          'background .12s, box-shadow .12s, transform .1s, border-color .12s',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// Hairline rule under section titles.
export const DoubleRule: React.FC<{ style?: React.CSSProperties }> = ({
  style,
}) => <div style={{ borderTop: '1px solid #383838', ...style }} />;

export const AmInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  mono?: boolean;
  // When provided: green underline once the field is valid; orange while focused; grey otherwise.
  valid?: boolean;
  style?: React.CSSProperties;
}> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  mono,
  valid,
  style,
}) => {
  const [focused, setFocused] = useState(false);
  const highlight = valid ? '#00B061' : focused ? '#f08416' : null;
  const borderColor = highlight ?? '#787878';
  const labelColor = highlight ?? '#9a9a9a';
  return (
    <label style={{ display: 'block', position: 'relative', ...style }}>
      <div
        style={{
          fontSize: 11,
          color: labelColor,
          textTransform: 'uppercase',
          letterSpacing: '.18em',
          marginBottom: 8,
          fontWeight: 500,
          transition: 'color .15s',
        }}
      >
        {label}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          width: '100%',
          background: 'transparent',
          color: '#fff',
          border: 'none',
          borderBottom: `1px solid ${borderColor}`,
          padding: '8px 0 10px 0',
          fontSize: 18,
          fontFamily: mono
            ? 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace'
            : 'Kanit,sans-serif',
          outline: 'none',
          letterSpacing: mono ? '.02em' : 'normal',
          boxSizing: 'border-box',
          transition: 'border-color .15s',
        }}
      />
    </label>
  );
};

export type ExchangeChipStatus =
  | 'untested'
  | 'testing'
  | 'pending'
  | 'ok'
  | 'fail';

export const ExchangeStatusChip: React.FC<{ state: ExchangeChipStatus }> = ({
  state,
}) => {
  const map: Record<
    ExchangeChipStatus,
    { color: string; label: string; glow: boolean }
  > = {
    untested: { color: '#9a9a9a', label: 'AWAITING TEST', glow: false },
    testing: { color: '#f08416', label: 'TESTING…', glow: true },
    pending: { color: '#f08416', label: 'NOT YET CONNECTED', glow: true },
    ok: { color: '#00B061', label: 'CONNECTED', glow: true },
    fail: { color: '#F0162F', label: 'CONNECTION FAILED', glow: true },
  };
  const m = map[state] || map.untested;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
        fontSize: 10,
        letterSpacing: '.2em',
        textTransform: 'uppercase',
        color: m.color,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          background: m.color,
          boxShadow: m.glow ? `0 0 8px ${m.color}` : 'none',
          border: '0.5px solid rgba(255,255,255,.3)',
        }}
      />
      {m.label}
    </span>
  );
};

export const ExchangeInstructions: React.FC<{
  exchange: ExchangeMeta;
  dim?: boolean;
  accent?: string;
}> = ({ exchange, dim, accent }) => {
  const c = accent || '#f08416';
  return (
    <ol
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {exchange.steps.map((tokens, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
            fontSize: 13,
            color: dim ? '#c4c1c1' : '#fff',
            lineHeight: 1.5,
          }}
        >
          <span
            style={{
              fontFamily:
                'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
              color: c,
              fontWeight: 700,
              flexShrink: 0,
              minWidth: 18,
            }}
          >
            {(i + 1).toString().padStart(2, '0')}
          </span>
          <span>
            {tokens.map((t, j) =>
              j % 2 === 1 ? (
                <b key={j} style={{ color: c, fontWeight: 600 }}>
                  {' '}
                  {t}{' '}
                </b>
              ) : (
                <span key={j}>{t}</span>
              ),
            )}
          </span>
        </li>
      ))}
    </ol>
  );
};
